import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addAdminNote,
  deactivateProfile,
  setProfileDemo,
  exportProfiles,
  getProfileDetail,
  getTimeToFirstDish,
  listChefListings,
  listProfiles,
  listProfileReviews,
  setBlueStarStatus,
  sendUserMessage,
  translateUserMessage,
  type BlueStarStatus,
  type SendUserMessageInput,
  type UserMessageLang,
  type DirectoryFilters,
  type DirectoryParams,
  type ListingsParams,
  type ReviewedAs,
} from './api';

export const profileKeys = {
  all: ['profiles'] as const,
  list: (params: DirectoryParams) => [...profileKeys.all, 'list', params] as const,
  /** KPI tiles: a pageSize=1 list read for its `total` (until BE ask B10). */
  count: (filters: DirectoryFilters) => [...profileKeys.all, 'count', filters] as const,
  detail: (id: string) => [...profileKeys.all, 'detail', id] as const,
  listings: (id: string, params: ListingsParams) => [...profileKeys.all, 'listings', id, params] as const,
  neverPublished: (localeIds: string[]) => [...profileKeys.all, 'never-published', localeIds] as const,
  reviews: (id: string, as: ReviewedAs, page: number) => [...profileKeys.all, 'reviews', id, as, page] as const,
};

export function useProfileDirectory(params: DirectoryParams, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: profileKeys.list(params),
    queryFn: ({ signal }) => listProfiles(params, signal),
    enabled: opts.enabled ?? true,
    placeholderData: keepPreviousData,
    // The table shows the error inline, with a retry.
    meta: { silent: true },
  });
}

/** A count for a KPI tile. Silent: a failed tile shows "—", not a modal per tile. */
export function useProfileCount(filters: DirectoryFilters) {
  return useQuery({
    queryKey: profileKeys.count(filters),
    queryFn: async ({ signal }) => (await listProfiles({ ...filters, page: 1, pageSize: 1 }, signal)).total,
    staleTime: 60_000,
    meta: { silent: true },
  });
}

export function useProfileDetail(id: string | undefined) {
  return useQuery({
    queryKey: profileKeys.detail(id ?? ''),
    queryFn: ({ signal }) => getProfileDetail(id ?? '', signal),
    enabled: !!id,
  });
}

export function useChefListings(id: string, params: ListingsParams) {
  return useQuery({
    queryKey: profileKeys.listings(id, params),
    queryFn: ({ signal }) => listChefListings(id, params, signal),
    placeholderData: keepPreviousData,
    meta: { silent: true },
  });
}

/** Reviews a profile received as a chef or as an eater. Silent: shown inline. */
export function useProfileReviews(id: string, as: ReviewedAs, page: number, pageSize = 10) {
  return useQuery({
    queryKey: profileKeys.reviews(id, as, page),
    queryFn: ({ signal }) => listProfileReviews(id, as, { page, pageSize }, signal),
    placeholderData: keepPreviousData,
    meta: { silent: true },
  });
}

/** Consumers with no published dish, oldest signup first (BE caps at 500). */
export function useNeverPublished(localeIds: string[], opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: profileKeys.neverPublished(localeIds),
    queryFn: ({ signal }) => getTimeToFirstDish({ localeIds, limit: 500 }, signal),
    enabled: opts.enabled ?? true,
    meta: { silent: true },
  });
}

/**
 * Writes invalidate every profile query: the row, the tiles and the open
 * dialog all read the same profile, and a change to one (a Blue Star approval
 * moves the "certified" tile) must not leave another stale.
 */
function useInvalidateProfiles() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: profileKeys.all });
}

export function useSetBlueStarStatus(id: string) {
  const invalidate = useInvalidateProfiles();
  return useMutation({
    mutationFn: (status: BlueStarStatus) => setBlueStarStatus(id, status),
    onSuccess: invalidate,
  });
}

export function useAddAdminNote(id: string) {
  const invalidate = useInvalidateProfiles();
  return useMutation({
    mutationFn: (text: string) => addAdminNote(id, text),
    onSuccess: invalidate,
  });
}

export function useSetProfileDemo(id: string) {
  const invalidate = useInvalidateProfiles();
  return useMutation({
    mutationFn: (demo: boolean) => setProfileDemo(id, demo),
    onSuccess: invalidate,
  });
}

export function useDeactivateProfile(id: string) {
  const invalidate = useInvalidateProfiles();
  return useMutation({
    mutationFn: (reason?: string) => deactivateProfile(id, reason),
    onSuccess: invalidate,
  });
}

/** No invalidation: a message doesn't change the profile. */
export function useSendUserMessage() {
  return useMutation({
    mutationFn: (input: SendUserMessageInput) => sendUserMessage(input),
  });
}

export function useTranslateUserMessage() {
  return useMutation({
    mutationFn: ({ text, lang }: { text: string; lang: UserMessageLang }) => translateUserMessage(text, lang),
  });
}

/** CSV of the directory for the current filters. A mutation so a 400 (over the row cap) reaches the error modal. */
export function useExportProfiles(resource: string) {
  return useMutation({
    mutationFn: (filters: DirectoryFilters) => exportProfiles(filters, resource),
  });
}
